import { useState } from "react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { CAMPAIGN_STATUS_LABELS } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Header, Hint, Section, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { BackLink, Footer, MessagePreview, Steps } from "@/components/campaigns/primitives"
import { campaignQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"
import { useCampaignUpdate } from "./w.$workspace.campaigns.$campaignId.audience"

export const Route = createFileRoute("/w/$workspace/campaigns/$campaignId/message")({ component: CampaignMessage })

/** Screen 26. */
function CampaignMessage() {
  const { workspace } = useWorkspaceContext()
  const { campaignId } = Route.useParams()
  const navigate = useNavigate()
  const campaign = useSuspenseQuery(campaignQuery(workspace.slug, campaignId)).data
  const [text, setText] = useState(campaign.message.text)
  const [label, setLabel] = useState(campaign.message.button?.label ?? "")
  const [url, setUrl] = useState(campaign.message.button?.url ?? "")
  const [preview, setPreview] = useState(false)
  const update = useCampaignUpdate(campaignId)
  const params = { workspace: workspace.slug, campaignId }
  const editable = campaign.status === "draft"
  const valid = text.trim().length > 0 && text.length <= 1000 && (!label.trim() || /^https:\/\/.+\..+/.test(url))
  const button = label.trim() ? { label: label.trim(), url } : null
  return (
    <div className="page-wrap au-root">
      <Header title={campaign.name} back={<BackLink to="/w/$workspace/campaigns" params={{ workspace: workspace.slug }}>Campaigns</BackLink>}>
        <Status>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Status>
        <Button variant="outline" onClick={() => setPreview(true)}>
          Preview message
        </Button>
      </Header>
      <Steps current="message" workspace={workspace.slug} campaignId={campaignId} />
      <div className="au-two-column">
        <Section title="Write your message" description="Make the next step clear and personal.">
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="campaign-message">Message</Label>
                <span className="text-xs text-muted-foreground tabular-nums">{text.length} / 1,000</span>
              </div>
              <Textarea id="campaign-message" value={text} maxLength={1000} disabled={!editable} onChange={(e) => setText(e.target.value)} className="min-h-56 leading-6" />
              <Button variant="ghost" size="sm" className="-ml-2" disabled={!editable} onClick={() => setText(`${text} {{first_name | there}}`)}>
                <Plus /> Add first name
              </Button>
              <p className="text-xs text-muted-foreground">If a first name is missing, “there” is used.</p>
            </div>
            <Separator />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="button-label">Button label</Label>
                <Input id="button-label" value={label} maxLength={24} disabled={!editable} onChange={(e) => setLabel(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="destination-url">Destination URL</Label>
                <Input id="destination-url" value={url} type="url" placeholder="https://" disabled={!editable} onChange={(e) => setUrl(e.target.value)} />
              </div>
            </div>
            <Hint>{valid ? "Content checks passed. Personalization has a fallback and the destination uses HTTPS." : "Add a message and a valid HTTPS destination for your button to continue."}</Hint>
          </div>
        </Section>
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-medium">Recipient preview</h2>
            <Badge variant="secondary">{providerLabel(campaign.provider)}</Badge>
          </div>
          <MessagePreview text={text} button={button} workspaceName={workspace.name} provider={campaign.provider} connectionLabel={campaign.connectionLabel} />
          <p className="mt-4 text-xs text-muted-foreground">Preview only. No message is sent.</p>
        </div>
      </div>
      <Footer
        back={<BackLink to="/w/$workspace/campaigns/$campaignId/audience" params={params}>Back</BackLink>}
        label="Continue to schedule"
        disabled={!valid || !editable}
        pending={update.isPending}
        onNext={async () => {
          await update.mutateAsync({ message: { text, button } })
          await navigate({ to: "/w/$workspace/campaigns/$campaignId/schedule", params })
        }}
      />
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Message preview</DialogTitle>
            <DialogDescription>This is how Maya will see your message.</DialogDescription>
          </DialogHeader>
          <MessagePreview text={text} button={button} workspaceName={workspace.name} provider={campaign.provider} connectionLabel={campaign.connectionLabel} />
        </DialogContent>
      </Dialog>
    </div>
  )
}
